import {
  CopilotDiffChatBlobActionsMenuItems,
  type CopilotDiffChatBlobActionsMenuItemsProps,
} from '@github-ui/copilot-code-chat/CopilotDiffChatBlobActionsMenuItems'
import type {RepositoryNWO} from '@github-ui/current-repository'
import type {RichDiff} from '@github-ui/diff-lines/types'
import {blobPath, deleteBlobPath, editBlobPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
import {canSoftNavToCodeView, isPullRequestsEnabled} from '@github-ui/repo-app-flags'
import {useMediaQuery} from '@github-ui/use-media-query'
import {
  CheckIcon,
  CommentIcon,
  CopilotIcon,
  CopyIcon,
  EyeIcon,
  FoldIcon,
  KebabHorizontalIcon,
  PencilIcon,
  ShieldLockIcon,
  TrashIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import styles from './BlobActionsMenu.module.css'

interface RichDiffToggleProps {
  richDiff?: RichDiff
  isRichDiff: boolean
  onToggle: () => void
}

interface BlobActionsMenuProps {
  oid: string
  path: string
  repo: RepositoryNWO
  branchName?: string
  isViewable?: boolean
  isDeleteable?: boolean
  isEditable?: boolean
  shouldShowEditOptions?: boolean
  copilotDiffChatProps?: CopilotDiffChatBlobActionsMenuItemsProps
  returnToPullRequestPath?: string
  // Responsive menu item props
  onCommentOnFile?: () => void
  onCopyPath?: () => void
  onExpandLines?: () => void
  areLinesExpanded?: boolean
  richDiffToggle?: RichDiffToggleProps
  codeownersText?: string
  codeownersUrl?: string
  menuButtonId?: string
  onAskAboutDiff?: () => void
}

export function BlobActionsMenu({
  oid,
  path,
  repo,
  branchName,
  isViewable = true,
  isDeleteable = true,
  isEditable = true,
  shouldShowEditOptions = true,
  copilotDiffChatProps,
  returnToPullRequestPath,
  onCommentOnFile,
  onCopyPath,
  onExpandLines,
  areLinesExpanded,
  richDiffToggle,
  codeownersText,
  codeownersUrl,
  menuButtonId,
  onAskAboutDiff,
}: BlobActionsMenuProps) {
  return (
    <ActionMenu>
      <ActionMenu.Anchor id={menuButtonId}>
        <IconButton icon={KebabHorizontalIcon} aria-label="More options" variant="invisible" size="small" />
      </ActionMenu.Anchor>

      <ActionMenu.Overlay>
        <ActionList>
          <ResponsiveMenuItems
            richDiffToggle={richDiffToggle}
            copilotDiffChatProps={copilotDiffChatProps}
            onAskAboutDiff={onAskAboutDiff}
            onCommentOnFile={onCommentOnFile}
            onCopyPath={onCopyPath}
            onExpandLines={onExpandLines}
            areLinesExpanded={areLinesExpanded}
          />

          <BlobLinks
            oid={oid}
            path={path}
            repo={repo}
            branchName={branchName}
            isViewable={isViewable}
            isDeleteable={isDeleteable}
            isEditable={isEditable}
            shouldShowEditOptions={shouldShowEditOptions}
            returnToPullRequestPath={returnToPullRequestPath}
          />
          <CodeownersMenuItem codeownersText={codeownersText} codeownersUrl={codeownersUrl} />
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function ResponsiveMenuItems({
  richDiffToggle,
  copilotDiffChatProps,
  onAskAboutDiff,
  onCommentOnFile,
  onCopyPath,
  onExpandLines,
  areLinesExpanded,
}: Pick<
  BlobActionsMenuProps,
  | 'richDiffToggle'
  | 'copilotDiffChatProps'
  | 'onAskAboutDiff'
  | 'onCommentOnFile'
  | 'onCopyPath'
  | 'onExpandLines'
  | 'areLinesExpanded'
>) {
  // Match the removed CSS utilities exactly: fixed-pixel breakpoints for
  // hide-on-medium-up/hide-medium-up, and rem-based regular viewport for hide-on-desktop.
  const isBelowMediumUp = useMediaQuery('(max-width: 543px)')
  const isBelowFixedMedium = useMediaQuery('(max-width: 767px)')
  const isBelowDesktop = useMediaQuery('(max-width: calc(48rem - 0.02px))')

  return (
    <>
      {isBelowMediumUp && richDiffToggle?.richDiff?.canToggleRichDiff && (
        <>
          <ActionList.Item onSelect={richDiffToggle.onToggle}>
            <ActionList.LeadingVisual>
              <span className={richDiffToggle.isRichDiff ? styles.hiddenIcon : undefined}>
                <CheckIcon />
              </span>
            </ActionList.LeadingVisual>
            Source diff
          </ActionList.Item>
          <ActionList.Item onSelect={richDiffToggle.onToggle}>
            <ActionList.LeadingVisual>
              <span className={!richDiffToggle.isRichDiff ? styles.hiddenIcon : undefined}>
                <CheckIcon />
              </span>
            </ActionList.LeadingVisual>
            Rich diff
          </ActionList.Item>
          <ActionList.Divider />
        </>
      )}

      {copilotDiffChatProps && (
        <>
          <CopilotDiffChatBlobActionsMenuItems {...copilotDiffChatProps} />
          <ActionList.Divider />
        </>
      )}

      {isBelowFixedMedium && onAskAboutDiff && (
        <>
          <ActionList.Item onSelect={onAskAboutDiff}>
            <ActionList.LeadingVisual>
              <CopilotIcon />
            </ActionList.LeadingVisual>
            Ask about this diff
          </ActionList.Item>
          <ActionList.Divider />
        </>
      )}

      {isBelowDesktop && onCommentOnFile && (
        <>
          <ActionList.Item onSelect={onCommentOnFile}>
            <ActionList.LeadingVisual>
              <CommentIcon />
            </ActionList.LeadingVisual>
            Comment on this file
          </ActionList.Item>
          <ActionList.Divider />
        </>
      )}

      {isBelowDesktop && onCopyPath && (
        <ActionList.Item onSelect={onCopyPath}>
          <ActionList.LeadingVisual>
            <CopyIcon />
          </ActionList.LeadingVisual>
          Copy file name
        </ActionList.Item>
      )}
      {isBelowDesktop && onExpandLines && (
        <ActionList.Item onSelect={onExpandLines}>
          <ActionList.LeadingVisual>{areLinesExpanded ? <FoldIcon /> : <UnfoldIcon />}</ActionList.LeadingVisual>
          <span>{areLinesExpanded ? 'Collapse non-diff lines' : 'Expand all lines'}</span>
        </ActionList.Item>
      )}
      {isBelowDesktop && (onCopyPath || onExpandLines) && <ActionList.Divider />}
    </>
  )
}

function CodeownersMenuItem({
  codeownersText,
  codeownersUrl,
}: Pick<BlobActionsMenuProps, 'codeownersText' | 'codeownersUrl'>) {
  const isBelowDesktop = useMediaQuery('(max-width: calc(48rem - 0.02px))')

  if (!isBelowDesktop || !codeownersText || !codeownersUrl) return null

  return (
    <>
      <ActionList.Divider />
      <ActionList.LinkItem href={codeownersUrl}>
        <ActionList.LeadingVisual>
          <ShieldLockIcon />
        </ActionList.LeadingVisual>
        {codeownersText}
      </ActionList.LinkItem>
    </>
  )
}

function BlobLinks({
  oid,
  path,
  repo,
  branchName,
  isViewable,
  isDeleteable,
  isEditable,
  shouldShowEditOptions,
  returnToPullRequestPath,
}: {
  oid: string
  path: string
  repo: RepositoryNWO
  branchName?: string
  isViewable?: boolean
  isDeleteable?: boolean
  isEditable?: boolean
  shouldShowEditOptions?: boolean
  returnToPullRequestPath?: string
}) {
  if (canSoftNavToCodeView() && isPullRequestsEnabled()) {
    return (
      <BlobReactLinks
        oid={oid}
        path={path}
        repo={repo}
        branchName={branchName}
        isViewable={isViewable}
        isDeleteable={isDeleteable}
        isEditable={isEditable}
        shouldShowEditOptions={shouldShowEditOptions}
        returnToPullRequestPath={returnToPullRequestPath}
      />
    )
  }

  return (
    <BlobHrefLinks
      oid={oid}
      path={path}
      repo={repo}
      branchName={branchName}
      isViewable={isViewable}
      isDeleteable={isDeleteable}
      isEditable={isEditable}
      shouldShowEditOptions={shouldShowEditOptions}
      returnToPullRequestPath={returnToPullRequestPath}
    />
  )
}

function BlobHrefLinks({
  oid,
  path,
  repo,
  branchName,
  isViewable,
  isDeleteable,
  isEditable,
  shouldShowEditOptions,
  returnToPullRequestPath,
}: {
  oid: string
  path: string
  repo: RepositoryNWO
  branchName?: string
  isViewable?: boolean
  isDeleteable?: boolean
  isEditable?: boolean
  shouldShowEditOptions?: boolean
  returnToPullRequestPath?: string
}) {
  return (
    <>
      <ActionList.LinkItem
        href={blobPath({
          repo: repo.name,
          owner: repo.ownerLogin,
          filePath: path,
          commitish: oid,
        })}
        inactiveText={isViewable ? undefined : 'Action unavailable'}
      >
        <ActionList.LeadingVisual>
          <EyeIcon />
        </ActionList.LeadingVisual>
        View file
      </ActionList.LinkItem>
      {branchName && shouldShowEditOptions && (
        <>
          <ActionList.LinkItem
            href={editBlobPath({
              repo: repo.name,
              owner: repo.ownerLogin,
              filePath: path,
              commitish: branchName,
              returnToPrPath: returnToPullRequestPath,
            })}
            inactiveText={isEditable ? undefined : 'Action unavailable'}
          >
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            Edit file
          </ActionList.LinkItem>
          <ActionList.LinkItem
            variant="danger"
            href={deleteBlobPath({
              repo: repo.name,
              owner: repo.ownerLogin,
              filePath: path,
              commitish: branchName,
            })}
            inactiveText={isDeleteable ? undefined : 'Action unavailable'}
          >
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            Delete file
          </ActionList.LinkItem>
        </>
      )}
    </>
  )
}

function BlobReactLinks({
  oid,
  path,
  repo,
  branchName,
  isViewable,
  isDeleteable,
  isEditable,
  shouldShowEditOptions,
  returnToPullRequestPath,
}: {
  oid: string
  path: string
  repo: RepositoryNWO
  branchName?: string
  isViewable?: boolean
  isDeleteable?: boolean
  isEditable?: boolean
  shouldShowEditOptions?: boolean
  returnToPullRequestPath?: string
}) {
  return (
    <>
      <ActionList.LinkItem
        as={Link}
        to={blobPath({
          repo: repo.name,
          owner: repo.ownerLogin,
          filePath: path,
          commitish: oid,
        })}
        inactiveText={isViewable ? undefined : 'Action unavailable'}
      >
        <ActionList.LeadingVisual>
          <EyeIcon />
        </ActionList.LeadingVisual>
        View file
      </ActionList.LinkItem>
      {branchName && shouldShowEditOptions && (
        <>
          <ActionList.LinkItem
            as={Link}
            to={editBlobPath({
              repo: repo.name,
              owner: repo.ownerLogin,
              filePath: path,
              commitish: branchName,
              returnToPrPath: returnToPullRequestPath,
            })}
            inactiveText={isEditable ? undefined : 'Action unavailable'}
          >
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            Edit file
          </ActionList.LinkItem>
          <ActionList.LinkItem
            as={Link}
            variant="danger"
            to={deleteBlobPath({
              repo: repo.name,
              owner: repo.ownerLogin,
              filePath: path,
              commitish: branchName,
            })}
            inactiveText={isDeleteable ? undefined : 'Action unavailable'}
          >
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            Delete file
          </ActionList.LinkItem>
        </>
      )}
    </>
  )
}
